import { useCallback, useState } from 'react';

import type { NoMaladaptiveBehaviorConfirmParams } from './types';

export default function useNoMaladaptiveBehaviorConfirm({
  hasSavedBehaviors,
  refetchCrisisPage,
  clearAll,
  setNoMaladaptiveBehavior,
}: NoMaladaptiveBehaviorConfirmParams) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState('');
  const [isResolving, setIsResolving] = useState(false);

  const canMarkNoMaladaptiveBehavior = useCallback(async () => {
    setIsResolving(true);
    try {
      const page = await refetchCrisisPage();
      if (page == null) {
        setBlockedMessage(
          'Crisis plans could not be loaded. Retry before marking no maladaptive behavior.',
        );
        return false;
      }
      if (page.crisis_plans.length > 0) {
        setBlockedMessage('Delete the crisis plan(s) first before checking this checkbox.');
        return false;
      }
      return true;
    } finally {
      setIsResolving(false);
    }
  }, [refetchCrisisPage]);

  const handleToggle = useCallback(
    async (checked: boolean) => {
      if (!checked) {
        void setNoMaladaptiveBehavior(false);
        return;
      }
      if (!(await canMarkNoMaladaptiveBehavior())) return;
      if (!hasSavedBehaviors) {
        void setNoMaladaptiveBehavior(true);
        return;
      }
      setIsConfirmOpen(true);
    },
    [canMarkNoMaladaptiveBehavior, hasSavedBehaviors, setNoMaladaptiveBehavior],
  );

  const handleConfirm = useCallback(async () => {
    if (!(await canMarkNoMaladaptiveBehavior())) {
      setIsConfirmOpen(false);
      return;
    }
    if (!(await clearAll())) return;
    if (await setNoMaladaptiveBehavior(true)) setIsConfirmOpen(false);
  }, [canMarkNoMaladaptiveBehavior, clearAll, setNoMaladaptiveBehavior]);

  const closeConfirm = useCallback(() => setIsConfirmOpen(false), []);
  const closeBlocked = useCallback(() => setBlockedMessage(''), []);

  return {
    isConfirmOpen,
    isBlockedOpen: blockedMessage !== '',
    blockedMessage,
    isResolving,
    handleToggle,
    handleConfirm,
    closeConfirm,
    closeBlocked,
  };
}
